// src/lib/sample-data/userOrganizationMapping.ts

import type { UserRoleType } from '@/lib/types';
import { SAMPLE_DATA_CONFIG } from './config';

/**
 * Mapping of Firebase user UIDs to their corresponding sample organizations
 * This ensures demo users are properly linked to sample organizations for demo purposes
 */

export interface UserOrganizationMapping {
  uid: string;
  email: string;
  displayName: string;
  organizationId: string;
  tenantId: string;
  roles: UserRoleType[];
  firstName: string;
  lastName: string;
  associatedClubIds?: string[];
}

// Get the default organization ID from sample data config
const defaultOrganizationId = SAMPLE_DATA_CONFIG.defaultOrganizationId; // '8392746510'

/**
 * Sample user to organization mappings for the demo tenant
 * These users should exist in Firebase Auth with tenant ID 'courtly-sampledata-m1g99'
 */
export const SAMPLE_USER_ORGANIZATION_MAPPINGS: UserOrganizationMapping[] = [
  {
    uid: '2847391650', // Roger Federer - Main admin user
    email: 'roger.federer@example.com',
    displayName: 'Roger Federer',
    firstName: 'Roger',
    lastName: 'Federer',
    organizationId: defaultOrganizationId, // Courtly Sports Inc.
    tenantId: defaultOrganizationId,
    roles: ['Admin'],
    associatedClubIds: [SAMPLE_DATA_CONFIG.defaultClubId], // Topspin Tennis Club
  },
  {
    uid: '4567891230', // John McEnroe - Global Padel Group
    email: 'john.mcenroe@globalpadel.com',
    displayName: 'John McEnroe',
    firstName: 'John',
    lastName: 'McEnroe',
    organizationId: '2951738460', // Global Padel Group
    tenantId: '2951738460',
    roles: ['Admin'],
    associatedClubIds: ['7384629150'], // Global Padel Club
  },
  {
    uid: '5678912340', // Venus Williams - Urban Tennis Centers
    email: 'venus.williams@urbantennis.org',
    displayName: 'Venus Williams',
    firstName: 'Venus',
    lastName: 'Williams',
    organizationId: '6174829350', // Urban Tennis Centers
    tenantId: '6174829350',
    roles: ['Admin'],
    associatedClubIds: ['4629385170', '2039485761', '3140596872', '4251607983'], // Elite Tennis Academy, Urban London, Urban New York, Urban Tokyo
  },
  {
    uid: '6789123450', // Amélie Mauresmo - Lagardère Paris Racing
    email: 'amelie.mauresmo@lagardereparisracing.com',
    displayName: 'Amélie Mauresmo',
    firstName: 'Amélie',
    lastName: 'Mauresmo',
    organizationId: '9516274830', // Lagardère Paris Racing
    tenantId: '9516274830',
    roles: ['Admin'],
    associatedClubIds: ['1928374650', '3748591620'], // Paris Racing, Multi-Sport Complex
  },
  {
    uid: 'superadmin001', // Laurent Christen - Super Admin (platform level)
    email: 'laurent.christen@gmail.com',
    displayName: 'Laurent Christen',
    firstName: 'Laurent',
    lastName: 'Christen',
    organizationId: '', // Platform level, no specific organization
    tenantId: 'platform',
    roles: ['Super Admin'],
    associatedClubIds: [],
  },
  {
    uid: '4051728396', // Maria Sharapova - Teacher user (existing UID from Firebase)
    email: 'maria.sharapova@example.com',
    displayName: 'Maria Sharapova',
    firstName: 'Maria',
    lastName: 'Sharapova',
    organizationId: defaultOrganizationId, // Courtly Sports Inc. (Russian Tennis Academy -> using default for now)
    tenantId: defaultOrganizationId,
    roles: ['Teacher'],
    associatedClubIds: [SAMPLE_DATA_CONFIG.defaultClubId], // Topspin Tennis Club
  },
  {
    uid: '9182736450', // Mark Guest - Guest user with restricted access
    email: 'guest@example.com',
    displayName: 'Mark Guest',
    firstName: 'Mark',
    lastName: 'Guest',
    organizationId: defaultOrganizationId, // Courtly Sports Inc.
    tenantId: defaultOrganizationId,
    roles: ['Guest'],
    associatedClubIds: [SAMPLE_DATA_CONFIG.defaultClubId], // Topspin Tennis Club
  }
];

/**
 * Get organization mapping for a Firebase user UID
 */
export function getUserOrganizationMapping(uid: string): UserOrganizationMapping | null {
  return SAMPLE_USER_ORGANIZATION_MAPPINGS.find(mapping => mapping.uid === uid) || null;
}

/**
 * Get organization mapping for a Firebase user email
 */
export function getUserOrganizationMappingByEmail(email: string): UserOrganizationMapping | null {
  return SAMPLE_USER_ORGANIZATION_MAPPINGS.find(mapping => mapping.email === email) || null;
}

/**
 * Get all users for a specific organization
 */
export function getUsersForOrganization(organizationId: string): UserOrganizationMapping[] {
  return SAMPLE_USER_ORGANIZATION_MAPPINGS.filter(mapping => mapping.organizationId === organizationId);
}

/**
 * Check if a user UID belongs to the sample data demo
 */
export function isSampleDataUser(uid: string): boolean {
  return SAMPLE_USER_ORGANIZATION_MAPPINGS.some(mapping => mapping.uid === uid);
}

/**
 * Get the Firebase tenant ID for sample data based on environment
 * Note: Sample data is ONLY used in development environment
 */
export function getSampleDataTenantId(): string {
  // Use the environment-specific tenant ID
  if (typeof window !== 'undefined') {
    return process.env.NEXT_PUBLIC_FIREBASE_TENANT_ID || 'courtly-sampledata-m1g89';
  }
  
  // Server-side fallback based on project ID
  // Only development environment uses sample data
  const projectId = process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID;
  switch (projectId) {
    case 'v1-courtly':
      return 'courtly-sampledata-m1g89';
    default:
      // Staging and production should never reach this function since sample data is disabled
      console.warn('Sample data function called in non-development environment');
      return 'courtly-sampledata-m1g89'; // Development fallback
  }
} 
